/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Input, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, finalize, of, switchMap} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {StateService} from '@uirouter/core';

import template from './signal-admin-resource-context-actions.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {AngularModalService} from '../modal/angular-modal.service';
import {AdminObjectState, SignalAdminResourceModel} from './signal-admin-resource-api';
import {SignalAdminResourcePageState} from './signal-admin-resource-page-state';
import {SignalAdminResourceSpecifyIdModalComponent} from './signal-admin-resource-specify-id-modal.component';
import {handleCopyModalResult} from '../util/handle-copy-modal-result';
import {ConfirmObjectStateModalComponent} from '../directives/confirm-object-state-modal.component';

const OBJECT_STATE_LABELS: Record<AdminObjectState, {title: string}> = {
  ACTIVE: {title: 'Activation'},
  INACTIVE: {title: 'Archiving'},
  HIDDEN: {title: 'Deletion'}
};

@Component({
  standalone: false,
  selector: 'adn-signal-admin-resource-context-actions',
  template
})
export class SignalAdminResourceContextActionsComponent {
  @Input() state: SignalAdminResourcePageState<SignalAdminResourceModel>;
  readonly changingState = signal<AdminObjectState | null>(null);
  readonly hideHidden: boolean;

  private readonly modal = inject(AngularModalService);
  private readonly toastr = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stateService = inject(StateService);

  constructor() {
    this.hideHidden = inject(LocalNetworkProfile).isPureMarketplacePlatform();
  }

  specifyId(): void {
    this.modal.show(SignalAdminResourceSpecifyIdModalComponent, {
      initialState: {state: this.state}
    });
  }

  copy(): void {
    const copyConfig = this.state.copyConfig;
    if (!copyConfig?.modalComponent || this.state.readOnly() || this.state.isNew()) {
      return;
    }
    const modalRef = this.modal.show(copyConfig.modalComponent, {
      initialState: {model: this.state.model()}
    });
    (modalRef.content as any).onCopied.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((result: any) => {
      handleCopyModalResult(() => {
        if (result.destination) {
          this.stateService.go(copyConfig.stateUrl, {id: result.model.id}, {inherit: false});
        } else {
          this.toastr.success(
            `A copy of this ${copyConfig.lcase} was successfully created.`,
            `${copyConfig.ucase} Copied`
          );
        }
      });
    });
  }

  setObjectState(objectState: AdminObjectState, event: Event): void {
    event.preventDefault();
    if (this.state.blockObjectStateChanges) {
      this.toastr.error(this.state.blockObjectStateChanges, 'State change failed');
      return;
    }
    if (this.isDisabled(objectState)) {
      return;
    }
    if (objectState !== 'ACTIVE' && this.state.confirmation) {
      const objectData = {...this.state.confirmation};
      if (objectData.warningMessage) {
        objectData.warningMessage = objectData.warningMessage.replace('{{action}}', OBJECT_STATE_LABELS[objectState].title);
      }
      const modalRef = this.modal.show(ConfirmObjectStateModalComponent, {
        initialState: {textData: OBJECT_STATE_LABELS[objectState], objectData}
      });
      (modalRef.content as ConfirmObjectStateModalComponent).confirmed.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.changeObjectState(objectState));
      return;
    }
    this.changeObjectState(objectState);
  }

  private changeObjectState(objectState: AdminObjectState): void {
    const pageState = this.state;
    const model = pageState.model();
    this.changingState.set(objectState);
    pageState.api.setObjectState(model.id, objectState).pipe(
      switchMap((updated) => pageState.api.get(model.id).pipe(
        catchError(() => of({...model, ...updated, objectState}))
      )),
      finalize(() => this.changingState.set(null)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (updated) => pageState.update({...model, ...updated, objectState}),
      error: (response) => {
        const message = typeof response === 'string' ? response : response?.message;
        if (message) {
          this.toastr.error(message, 'State change failed');
        }
      }
    });
  }

  isDisabled(objectState: AdminObjectState): boolean {
    return this.state.readOnly()
      || this.state.isNew()
      || !!this.state.blockObjectStateChanges
      || !!this.changingState()
      || this.state.model().objectState === objectState;
  }
}
